import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { dashboardApi, DashboardApiError } from '../../lib/apiClient';
import {
  type BoUser,
  canAccessDashboardRoute,
  defaultDashboardRoute,
} from '../../lib/permissions';
import { clearAuthenticatedQueryCache } from '../../lib/queryClient';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import { reportAppError } from '../errors/ErrorPopupProvider';

// Custom inline SVG icons for visual details and zero dependencies
const UserIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PackageIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <polygon points="12 22.08 12 12 3 6.92 3 17.16 12 22.08" />
    <polygon points="12 22.08 12 12 21 6.92 21 17.16 12 22.08" />
    <polygon points="12 12 3 6.92 12 1.8 21 6.92 12 12" />
  </svg>
);

const TrendingUpIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TagIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const ShieldIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export function PasswordLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const login = useSession((s) => s.login);
  const setBranch = useUi((s) => s.setBranch);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nextPathForUser = (user: BoUser): string =>
    canAccessDashboardRoute(user, from) ? from : defaultDashboardRoute(user);
  const setScopeFromUser = (user: BoUser) => {
    setBranch(user.role === 'owner' ? null : (user.branchIds[0] ?? null));
  };

  const commitSession = (
    user: BoUser,
    token?: string | null,
    refreshToken?: string | null,
    tokenExpiresAt?: string | null,
  ) => {
    clearAuthenticatedQueryCache();
    login(
      {
        id: user.id,
        name: user.name,
        role: user.role,
        branchIds: user.branchIds,
        permissionOverrides: user.permissionOverrides,
      },
      token,
      refreshToken,
      tokenExpiresAt,
    );
    setScopeFromUser(user);
    navigate(nextPathForUser(user), { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    const trimmedPhone = phone.trim();
    if (!trimmedPhone) {
      setError('เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง');
      setIsSubmitting(false);
      return;
    }
    if (password.length < 8) {
      setError('เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง');
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await dashboardApi.login({
        phone: trimmedPhone,
        password,
        tenantId: import.meta.env.VITE_API_TENANT_ID ?? '',
      });

      commitSession(
        {
          id: result.user.id,
          name: result.user.name,
          role: result.user.role,
          branchIds: result.user.branchIds,
          permissionOverrides: result.user.permissionOverrides,
        },
        result.token,
        result.refreshToken,
        result.expiresAt,
      );
    } catch (caught) {
      if (
        caught instanceof DashboardApiError &&
        ['invalid_credentials', 'auth_error', 'auth_required'].includes(
          caught.code,
        )
      ) {
        reportAppError(
          new DashboardApiError({
            code: caught.code,
            message: 'เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง',
            status: caught.status,
            requestId: caught.requestId,
            endpoint: '/auth/login',
          }),
          { endpoint: '/auth/login' },
        );
        setError('เบอร์โทรหรือรหัสผ่านไม่ถูกต้อง');
        return;
      }
      if (
        caught instanceof DashboardApiError &&
        caught.code === 'rate_limited'
      ) {
        reportAppError(caught, { endpoint: '/auth/login' });
        const retryAfterMs =
          typeof caught.details?.retryAfterMs === 'number'
            ? caught.details.retryAfterMs
            : 60_000;
        setError(
          `พยายามเข้าสู่ระบบหลายครั้งเกินไป กรุณาลองใหม่ใน ${Math.ceil(
            retryAfterMs / 1000,
          )} วินาที`,
        );
        return;
      }
      reportAppError(caught, { endpoint: '/auth/login' });
      setError(
        caught instanceof Error
          ? caught.message
          : 'ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12 bg-bg-warm font-sans">
      {/* Left panel: Brand & Features Showcase Grid (Visible only on desktop) */}
      <div className="hidden lg:flex lg:col-span-7 bg-ink text-white p-16 flex-col justify-between relative overflow-hidden">
        {/* Animated ambient mesh blobs */}
        <div
          className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] bg-accent/25 rounded-full blur-[120px] pointer-events-none animate-pulse"
          style={{ animationDuration: '8s' }}
        />
        <div
          className="absolute bottom-[-20%] right-[-10%] w-[75%] h-[75%] bg-primary-light/20 rounded-full blur-[100px] pointer-events-none animate-pulse"
          style={{ animationDuration: '12s' }}
        />

        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)] pointer-events-none" />

        {/* Brand Header */}
        <div className="flex items-center gap-3 relative z-10">
          <img
            src="/pangsuay_logo.png"
            alt="โลโก้แป้งสวย POS"
            className="w-12 h-12 rounded-xl bg-white object-contain shadow-lg"
          />
          <div>
            <div className="text-h3 font-bold tracking-wide">ร้านแป้งสวย</div>
            <div className="text-sm-label text-primary-light/80 font-bold ">
              Backoffice Dashboard
            </div>
          </div>
        </div>

        {/* Hero Copy & 2x2 Feature Showcase Grid */}
        <div className="my-auto space-y-8 relative z-10 max-w-xl">
          <div className="space-y-4">
            <h1 className="text-[40px] font-bold leading-tight font-sans-thai text-white">
              ระบบหลังบ้านอัจฉริยะ
              <br />
              จัดการทุกเรื่องง่ายในหนึ่งเดียว
            </h1>
            <p className="text-body text-white/80 leading-relaxed font-sans-thai max-w-lg">
              ควบคุมดูแลคลังสินค้า ยอดขายรายวัน รายงานภาษีมูลค่าเพิ่ม โปรโมชั่น
              และพนักงานจากหน้าจอเดียว
              เพื่อการบริหารและวิเคราะห์ธุรกิจที่รวดเร็ว แม่นยำ และปลอดภัย
            </p>
          </div>

          {/* SaaS-style Feature Showcase (No privacy leak, entirely info-based) */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            {/* Feature 1: Stock Control */}
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 flex gap-4 items-start shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                <PackageIcon className="w-5 h-5 text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="text-[14px] font-bold text-white">
                  คลังสินค้าแม่นยำ
                </h3>
                <p className="text-sm-label text-white/70 leading-normal">
                  ควบคุมสต็อก อัปเดตการเข้า-ออก
                  และโอนย้ายสินค้าระหว่างสาขาเรียลไทม์
                </p>
              </div>
            </div>

            {/* Feature 2: Analytics & Reports */}
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 flex gap-4 items-start shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                <TrendingUpIcon className="w-5 h-5 text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="text-[14px] font-bold text-white">
                  วิเคราะห์ยอดขาย
                </h3>
                <p className="text-sm-label text-white/70 leading-normal">
                  รายงานผลรายวัน/รายเดือน และสรุปยอดส่งภาษีมูลค่าเพิ่ม (VAT)
                  ได้อย่างง่ายดาย
                </p>
              </div>
            </div>

            {/* Feature 3: Promotions */}
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 flex gap-4 items-start shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                <TagIcon className="w-5 h-5 text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="text-[14px] font-bold text-white">
                  แคมเปญโปรโมชั่น
                </h3>
                <p className="text-sm-label text-white/70 leading-normal">
                  ออกแบบเงื่อนไขพิเศษ ส่วนลด หรือของแถม
                  ช่วยกระตุ้นยอดขายหน้าร้าน POS
                </p>
              </div>
            </div>

            {/* Feature 4: Security Access */}
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 flex gap-4 items-start shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                <ShieldIcon className="w-5 h-5 text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="text-[14px] font-bold text-white">
                  ความปลอดภัยสูงสุด
                </h3>
                <p className="text-sm-label text-white/70 leading-normal">
                  กำหนดสิทธิ์แยกตำแหน่งผู้จัดการและแคชเชียร์ พร้อมระบบบันทึก
                  Audit Logs
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Footer */}
        <div className="text-xs text-white/50 relative z-10 font-mono">
          © 2026 PangSuay POS Backoffice. All Rights Reserved.
        </div>
      </div>

      {/* Right panel: Login form */}
      <div className="lg:col-span-5 flex items-center justify-center p-6 sm:p-12 bg-bg-warm">
        <div className="w-full max-w-md bg-surface shadow-xl rounded-2xl border border-line-2 p-8 sm:p-10 transition-all duration-300">
          {/* Logo showing ONLY on mobile */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img
              src="/pangsuay_logo.png"
              alt="โลโก้แป้งสวย POS"
              className="w-12 h-12 rounded-xl bg-white object-contain shadow-lg mb-3"
            />
            <h1 className="text-h2 font-bold text-primary">ร้านแป้งสวย</h1>
            <p className="text-sm-label text-ink-3 mt-1">
              ระบบหลังร้าน (Backoffice)
            </p>
          </div>

          {/* Form Title (Only visible on desktop) */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-h2 font-bold text-ink">ลงชื่อเข้าใช้งาน</h2>
            <p className="text-body text-ink-3 mt-1.5">
              ป้อนข้อมูลประจำตัวของคุณเพื่อเปิดแดชบอร์ด
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            {/* Phone input group */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="phone"
                className="text-sm-label font-bold text-ink-2"
              >
                เบอร์โทร
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-ink-3">
                  <UserIcon className="w-4.5 h-4.5" />
                </span>
                <input
                  id="phone"
                  name="phone"
                  aria-label="เบอร์โทร"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full h-11 pl-10 pr-3 rounded-btn border border-line bg-surface text-body text-ink transition-all duration-200 hover:border-ink-3 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                  placeholder="0812345678"
                  autoComplete="tel"
                  required
                />
              </div>
            </div>

            {/* Password input group */}
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-center">
                <label
                  htmlFor="password"
                  className="text-sm-label font-bold text-ink-2"
                >
                  รหัสผ่าน
                </label>
                <span className="text-sm-label font-semibold text-ink-3">
                  ติดต่อผู้จัดการเพื่อรีเซ็ตรหัสผ่าน
                </span>
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-ink-3">
                  <LockIcon className="w-4.5 h-4.5" />
                </span>
                <input
                  id="password"
                  name="password"
                  aria-label="รหัสผ่าน"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-10 pr-10 rounded-btn border border-line bg-surface text-body text-ink transition-all duration-200 hover:border-ink-3 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-3 hover:text-ink transition-colors focus:outline-none"
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-4.5 h-4.5" />
                  ) : (
                    <EyeIcon className="w-4.5 h-4.5" />
                  )}
                </button>
              </div>
            </div>

            {/* Error messaging */}
            {error && (
              <div className="flex items-center gap-2.5 p-3 bg-danger/10 border border-danger/20 rounded-btn text-sm-label text-danger font-semibold bo-fade">
                <svg
                  className="w-4 h-4 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Submit button */}
            <Btn
              kind="primary"
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 justify-center text-body shadow-lg shadow-primary/15 hover:shadow-xl hover:shadow-primary/25 transition-all duration-200"
            >
              {isSubmitting ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </Btn>
          </form>
        </div>
      </div>
    </div>
  );
}
